import React, { useCallback, useEffect, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Seasons tab (epic #307 phase 2): create/edit the season container
// (name, sport, window, status, notes), pick which nights are in it
// from the events dated inside the window, and read the live
// standings with a copyable shout-out for the main chat / outbox.
// Week 4: draft teams here (snake draft from the standings, then
// adjust by hand); games from the "team play starts" night on count
// for teams. Mark the season "ended" and the final shout-out appears.
// Standings themselves are computed from the compete log — this tab
// never writes scores. Entry fees (phase 3): the door prices live on
// the season (member / guest / ladies, and how much of each paying
// entry feeds the prize pot); what captains collect shows up here per
// night and in total.

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const SEASON_FIELDS = `seasonId name sport startDate endDate status eventIds notes createdAt updatedAt
  teamsFrom teams { teamId name color captainSub members { userSub name } }
  entryMemberFee entryGuestFee entryLadiesFee entryPrizePerEntry`;

const LIST_QUERY = `query AdminSeasons { seasons(includeArchived: true) { ${SEASON_FIELDS} } }`;

const DETAIL_QUERY = `query AdminSeasonDetail($seasonId: ID!) {
  seasonStandings(seasonId: $seasonId) {
    nightsTotal nightsPlayed playersCount gamesCounted leaderName shoutout
    standings { rank userSub name nightsPlayed gameWins gameLosses boardWins teamName }
    nights { socialEventId title date rsvpYes approvedPlayers gamesCounted gamesPending gamesDisputed leaderName leaderBoardWins entriesPaid entryCollected entryPot }
    entriesPaid entryCollected entryPot entryHouse
    monthlyChampions { month label name boardWins runnerUpName runnerUpBoardWins players }
    teamsFrom teamNightsPlayed teamShoutout
    teamStandings { rank teamId name color captainName boardWins gameWins gameLosses nightsPlayed members { userSub name boardWins } }
    final { championName championBoardWins runnerUpName teamChampionName teamChampionBoardWins shoutout }
  }
  seasonEventCandidates(seasonId: $seasonId) { socialEventId title date inSeason }
}`;

const UPSERT = `mutation UpsertSeason($input: SeasonInput!) {
  upsertSeason(input: $input) { ${SEASON_FIELDS} }
}`;

const SET_TEAMS = `mutation SetSeasonTeams($seasonId: ID!, $teamsFrom: String, $teams: [SeasonTeamInput!]!) {
  setSeasonTeams(seasonId: $seasonId, teamsFrom: $teamsFrom, teams: $teams) { ${SEASON_FIELDS} }
}`;

const MAX_TEAMS = 12;
const blankTeam = (n) => ({ teamId: "", name: `Team ${n}`, color: "", captainSub: "", members: [] });
const teamsOf = (s) =>
  ((s && s.teams) || []).map((t) => ({
    teamId: t.teamId,
    name: t.name || "",
    color: t.color || "",
    captainSub: t.captainSub || "",
    members: (t.members || []).map((m) => ({ userSub: m.userSub, name: m.name || "" })),
  }));

// Snake draft: ranked players go 1→N, then N→1, and so on. Keeps the
// existing team names/colors when the count matches.
const snakeDraft = (players, count, existing) => {
  const teams = [];
  for (let i = 0; i < count; i += 1) {
    const prev = existing[i];
    teams.push(prev ? { ...prev, captainSub: "", members: [] } : blankTeam(i + 1));
  }
  players.forEach((p, idx) => {
    const round = Math.floor(idx / count);
    const pos = idx % count;
    const slot = round % 2 === 0 ? pos : count - 1 - pos;
    teams[slot].members.push({ userSub: p.userSub, name: p.name || "" });
  });
  return teams;
};

const STATUSES = ["upcoming", "active", "ended", "archived"];

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};
const muted = { color: "#6a7385", fontSize: 13 };
const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const input = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid #d0d6e0",
  borderRadius: 6,
  fontSize: 14,
  boxSizing: "border-box",
};
const label = { fontSize: 12, color: "#6a7385", fontWeight: 600, marginBottom: 4 };
const btn = (primary) => ({
  padding: "8px 14px",
  background: primary ? OCEAN : "#fff",
  color: primary ? "#fff" : "#333",
  border: primary ? "none" : "1px solid #d7dbe4",
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  marginRight: 8,
});
const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
  marginRight: 8,
  marginBottom: 8,
});

const Stat = ({ label: l, value, sub }) => (
  <div style={{ ...card, marginBottom: 0, flex: "1 1 120px", padding: "10px 12px" }}>
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{l}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const emptyForm = () => ({
  seasonId: "",
  name: "",
  sport: "Pickleball",
  startDate: "",
  endDate: "",
  status: "upcoming",
  notes: "",
  eventIds: [],
  entryMemberFee: "",
  entryGuestFee: "",
  entryLadiesFee: "",
  entryPrizePerEntry: "",
});

const feeField = (v) => (v === null || v === undefined ? "" : String(v));
const feeValue = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const toForm = (s) => ({
  seasonId: s.seasonId,
  name: s.name || "",
  sport: s.sport || "",
  startDate: s.startDate || "",
  endDate: s.endDate || "",
  status: s.status || "active",
  notes: s.notes || "",
  eventIds: s.eventIds || [],
  entryMemberFee: feeField(s.entryMemberFee),
  entryGuestFee: feeField(s.entryGuestFee),
  entryLadiesFee: feeField(s.entryLadiesFee),
  entryPrizePerEntry: feeField(s.entryPrizePerEntry),
});

const SeasonsTab = () => {
  const [seasons, setSeasons] = useState([]);
  const [selected, setSelected] = useState(null); // seasonId or "new"
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [teamsFrom, setTeamsFrom] = useState("");
  const [teams, setTeams] = useState([]);
  const [teamCount, setTeamCount] = useState(2);
  const [teamsBusy, setTeamsBusy] = useState(false);

  const loadTeamState = (s) => {
    const list = teamsOf(s);
    setTeams(list);
    setTeamsFrom((s && s.teamsFrom) || "");
    if (list.length) setTeamCount(list.length);
  };

  const loadList = useCallback(async () => {
    const data = await executeQuery(LIST_QUERY);
    const list = data.seasons || [];
    setSeasons(list);
    return list;
  }, []);

  const loadDetail = useCallback(async (seasonId) => {
    const data = await executeQuery(DETAIL_QUERY, { seasonId });
    setDetail(data.seasonStandings);
    setCandidates(data.seasonEventCandidates || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const list = await loadList();
        const first = list.find((s) => s.status !== "archived") || list[0];
        if (first) {
          setSelected(first.seasonId);
          setForm(toForm(first));
          loadTeamState(first);
          await loadDetail(first.seasonId);
        } else {
          setSelected("new");
        }
        setError("");
      } catch (err) {
        setError(err.message || "Could not load seasons");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadList, loadDetail]);

  const pick = async (seasonId) => {
    setNotice("");
    setError("");
    if (seasonId === "new") {
      setSelected("new");
      setForm(emptyForm());
      loadTeamState(null);
      setDetail(null);
      setCandidates([]);
      return;
    }
    const s = seasons.find((x) => x.seasonId === seasonId);
    if (!s) return;
    setSelected(seasonId);
    setForm(toForm(s));
    loadTeamState(s);
    setDetail(null);
    try {
      await loadDetail(seasonId);
    } catch (err) {
      setError(err.message || "Could not load that season");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleEvent = (socialEventId) =>
    setForm((f) => ({
      ...f,
      eventIds: f.eventIds.includes(socialEventId)
        ? f.eventIds.filter((id) => id !== socialEventId)
        : [...f.eventIds, socialEventId],
    }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        name: form.name,
        sport: form.sport || null,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
        notes: form.notes || null,
        eventIds: form.eventIds,
        entryMemberFee: feeValue(form.entryMemberFee),
        entryGuestFee: feeValue(form.entryGuestFee),
        entryLadiesFee: feeValue(form.entryLadiesFee),
        entryPrizePerEntry: feeValue(form.entryPrizePerEntry),
      };
      if (selected !== "new" && form.seasonId) payload.seasonId = form.seasonId;
      const data = await executeMutation(UPSERT, { input: payload });
      const saved = data.upsertSeason;
      await loadList();
      setSelected(saved.seasonId);
      setForm(toForm(saved));
      loadTeamState(saved);
      await loadDetail(saved.seasonId);
      setNotice(`Saved. Members see it at /seasons/${saved.seasonId}`);
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // ---- team draft -----------------------------------------------------
  const setTeam = (idx, patch) =>
    setTeams((list) => list.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

  const assign = (player, teamIdx) =>
    setTeams((list) =>
      list.map((t, i) => {
        const without = t.members.filter((m) => m.userSub !== player.userSub);
        if (i !== teamIdx) {
          return {
            ...t,
            members: without,
            captainSub: t.captainSub === player.userSub ? "" : t.captainSub,
          };
        }
        return { ...t, members: [...without, { userSub: player.userSub, name: player.name || "" }] };
      })
    );

  const removeTeam = (idx) => setTeams((list) => list.filter((_, i) => i !== idx));

  const addTeam = () =>
    setTeams((list) => (list.length >= MAX_TEAMS ? list : [...list, blankTeam(list.length + 1)]));

  const saveTeams = async (nextTeams) => {
    if (selected === "new" || !form.seasonId) return;
    setTeamsBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = (nextTeams || teams).map((t) => ({
        teamId: t.teamId || null,
        name: t.name,
        color: t.color || null,
        captainSub: t.captainSub || null,
        members: t.members.map((m) => ({ userSub: m.userSub, name: m.name || null })),
      }));
      const data = await executeMutation(SET_TEAMS, {
        seasonId: form.seasonId,
        teamsFrom: payload.length ? teamsFrom || null : null,
        teams: payload,
      });
      const saved = data.setSeasonTeams;
      await loadList();
      loadTeamState(saved);
      await loadDetail(saved.seasonId);
      setNotice(
        payload.length
          ? `Teams saved. Games from ${saved.teamsFrom} on count for teams.`
          : "Team play cleared."
      );
    } catch (err) {
      setError(err.message || "Saving teams failed");
    } finally {
      setTeamsBusy(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${what} copied.`);
    } catch (err) {
      setError("Copy failed — select the text and copy it by hand.");
    }
  };

  const standingsUrl =
    selected && selected !== "new"
      ? `${window.location.origin}/seasons/${selected}`
      : "";

  if (loading) return <p style={muted}>Loading seasons…</p>;

  // Candidates inside the window plus any already-chosen ids that
  // fall outside it (window edited after picking), so nothing picked
  // can silently vanish from the checklist.
  const candidateIds = new Set(candidates.map((c) => c.socialEventId));
  const extraChosen = form.eventIds
    .filter((id) => !candidateIds.has(id))
    .map((id) => ({ socialEventId: id, title: null, date: id.slice(0, 10) }));
  const checklist = [...candidates, ...extraChosen];

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        {seasons.map((s) => (
          <button
            key={s.seasonId}
            type="button"
            style={pill(selected === s.seasonId)}
            onClick={() => pick(s.seasonId)}
          >
            {s.status === "archived" ? "🗄️ " : ""}
            {s.name}
          </button>
        ))}
        <button type="button" style={pill(selected === "new")} onClick={() => pick("new")}>
          + New season
        </button>
      </div>

      {error && (
        <div style={{ ...card, borderColor: "#f3c2c2", color: "#b02a2a" }}>{error}</div>
      )}
      {notice && (
        <div style={{ ...card, background: SEAFOAM, borderColor: "#bfe0dc" }}>{notice}</div>
      )}

      {detail && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
            <Stat label="Nights" value={`${detail.nightsPlayed}/${detail.nightsTotal}`} sub="played / scheduled" />
            <Stat label="Players" value={detail.playersCount} sub="with counted games" />
            <Stat label="Games" value={detail.gamesCounted} sub="counted" />
            <Stat label="Leader" value={detail.leaderName || "—"} sub={detail.standings[0] ? `${detail.standings[0].boardWins} wins` : "no games yet"} />
            {(detail.entriesPaid > 0 || form.entryMemberFee !== "" || form.entryGuestFee !== "") && (
              <Stat
                label="Entry fees"
                value={`$${detail.entryCollected}`}
                sub={`${detail.entriesPaid} paid · pot $${detail.entryPot} · house $${detail.entryHouse}`}
              />
            )}
          </div>

          <div style={card}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <strong>Members' link:</strong>
              <code style={{ fontSize: 13 }}>{standingsUrl}</code>
              <button type="button" style={btn(false)} onClick={() => copy(standingsUrl, "Link")}>
                Copy link
              </button>
              {detail.shoutout && (
                <button type="button" style={btn(true)} onClick={() => copy(detail.shoutout, "Shout-out")}>
                  Copy standings shout-out
                </button>
              )}
              {detail.teamShoutout && (
                <button type="button" style={btn(false)} onClick={() => copy(detail.teamShoutout, "Team shout-out")}>
                  Copy team shout-out
                </button>
              )}
              {detail.final && (
                <button type="button" style={btn(true)} onClick={() => copy(detail.final.shoutout, "Final shout-out")}>
                  Copy final shout-out
                </button>
              )}
            </div>
            {detail.final && (
              <pre
                style={{
                  marginTop: 10,
                  marginBottom: 0,
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  background: "#fff8e1",
                  border: "1px solid #f1dd9a",
                  padding: 10,
                  borderRadius: 6,
                }}
              >
                {detail.final.shoutout}
              </pre>
            )}
            {detail.shoutout && (
              <pre
                style={{
                  marginTop: 10,
                  marginBottom: 0,
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  background: "#f7f8fa",
                  padding: 10,
                  borderRadius: 6,
                }}
              >
                {detail.shoutout}
              </pre>
            )}
            {detail.teamShoutout && (
              <pre
                style={{
                  marginTop: 10,
                  marginBottom: 0,
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  background: "#f7f8fa",
                  padding: 10,
                  borderRadius: 6,
                }}
              >
                {detail.teamShoutout}
              </pre>
            )}
            {detail.monthlyChampions.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 14 }}>
                {detail.monthlyChampions.map((m) => (
                  <div key={m.month}>
                    <span role="img" aria-label="trophy">🏆</span>{" "}
                    <strong>{m.label}:</strong> {m.name || "Member"} — {m.boardWins} wins
                    {m.runnerUpName ? ` (runner-up ${m.runnerUpName}, ${m.runnerUpBoardWins})` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <form onSubmit={save} style={card}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>
          {selected === "new" ? "New season" : "Edit season"}
          {selected !== "new" && form.seasonId && (
            <span style={{ ...muted, marginLeft: 8 }}>id: {form.seasonId}</span>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>Name</div>
            <input style={input} value={form.name} onChange={set("name")} placeholder="Pickleball Season 1 — Fall 2026" required maxLength={80} />
          </div>
          <div>
            <div style={label}>Sport</div>
            <input style={input} value={form.sport} onChange={set("sport")} placeholder="Pickleball" maxLength={40} />
          </div>
          <div>
            <div style={label}>Status</div>
            <select style={input} value={form.status} onChange={set("status")}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={label}>First day</div>
            <input style={input} type="date" value={form.startDate} onChange={set("startDate")} required />
          </div>
          <div>
            <div style={label}>Last day</div>
            <input style={input} type="date" value={form.endDate} onChange={set("endDate")} required />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={label}>Notes (shown to members)</div>
            <input style={input} value={form.notes} onChange={set("notes")} placeholder="Tuesdays 7pm · Fort Family courts · final on Dec 16" maxLength={500} />
          </div>
          <div style={{ gridColumn: "1 / -1", ...muted, marginBottom: -4 }}>
            Entry fees at the door (whole dollars; leave blank for no fees). Captains record what each
            player paid from the Season night tab; members see the prices on the board.
          </div>
          <div>
            <div style={label}>Member entry $</div>
            <input style={input} type="number" min={0} max={500} step={1} value={form.entryMemberFee} onChange={set("entryMemberFee")} placeholder="5" />
          </div>
          <div>
            <div style={label}>Guest entry $ (not paying)</div>
            <input style={input} type="number" min={0} max={500} step={1} value={form.entryGuestFee} onChange={set("entryGuestFee")} placeholder="10" />
          </div>
          <div>
            <div style={label}>Ladies entry $ (blank = guest rate)</div>
            <input style={input} type="number" min={0} max={500} step={1} value={form.entryLadiesFee} onChange={set("entryLadiesFee")} placeholder="0" />
          </div>
          <div>
            <div style={label}>Prize pot per paying entry $</div>
            <input style={input} type="number" min={0} max={500} step={1} value={form.entryPrizePerEntry} onChange={set("entryPrizePerEntry")} placeholder="3" />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={label}>
            Nights in this season ({form.eventIds.length} picked)
          </div>
          {selected === "new" ? (
            <p style={{ ...muted, marginBottom: 0 }}>
              Save the season first; events dated inside its window then
              appear here to pick from.
            </p>
          ) : checklist.length === 0 ? (
            <p style={{ ...muted, marginBottom: 0 }}>
              No events dated between {form.startDate || "?"} and {form.endDate || "?"} yet.
              Create the tourney nights on the calendar first, then come back and tick them.
            </p>
          ) : (
            <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid #e6e9f0", borderRadius: 6, padding: 8 }}>
              {checklist.map((c) => (
                <label key={c.socialEventId} style={{ display: "block", fontSize: 14, padding: "3px 0", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.eventIds.includes(c.socialEventId)}
                    onChange={() => toggleEvent(c.socialEventId)}
                    style={{ marginRight: 8 }}
                  />
                  {c.title || c.socialEventId}
                  <span style={{ ...muted, marginLeft: 6 }}>{c.date}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <button type="submit" style={btn(true)} disabled={busy}>
            {busy ? "Saving…" : selected === "new" ? "Create season" : "Save changes"}
          </button>
          <span style={muted}>
            Archived seasons disappear for members. Standings are never stored — they come from the nights' boards.
          </span>
        </div>
      </form>

      {detail && selected !== "new" && (() => {
        const assigned = new Map();
        teams.forEach((t, i) => t.members.forEach((m) => assigned.set(m.userSub, { idx: i, name: m.name })));
        const savedTeams = teamsOf(seasons.find((x) => x.seasonId === selected));
        const pool = detail.standings.map((r) => ({ userSub: r.userSub, name: r.name || "", boardWins: r.boardWins }));
        const poolSubs = new Set(pool.map((p) => p.userSub));
        for (const [sub, a] of assigned.entries()) {
          if (!poolSubs.has(sub)) pool.push({ userSub: sub, name: a.name, boardWins: 0 });
        }
        return (
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              <span role="img" aria-label="team">👥</span> Team play (week-4 draft)
            </div>
            <p style={{ ...muted, marginBottom: 10 }}>
              Pick the first night that counts for teams, draft from the standings
              (1→N, then N→1), fix anything by hand, save. Individual standings keep
              running for the whole season; the team table only counts games from
              that night on.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 10 }}>
              <div>
                <div style={label}>Team play starts</div>
                <input
                  style={{ ...input, width: 170 }}
                  type="date"
                  value={teamsFrom}
                  min={form.startDate || undefined}
                  max={form.endDate || undefined}
                  onChange={(e) => setTeamsFrom(e.target.value)}
                />
              </div>
              <div>
                <div style={label}>Teams</div>
                <input
                  style={{ ...input, width: 70 }}
                  type="number"
                  min={2}
                  max={MAX_TEAMS}
                  value={teamCount}
                  onChange={(e) => setTeamCount(Math.max(2, Math.min(MAX_TEAMS, Number(e.target.value) || 2)))}
                />
              </div>
              <button
                type="button"
                style={btn(false)}
                disabled={!pool.length}
                onClick={() => setTeams(snakeDraft(pool, teamCount, teams))}
              >
                Draft from standings
              </button>
              <button type="button" style={btn(false)} onClick={addTeam} disabled={teams.length >= MAX_TEAMS}>
                + Add team
              </button>
            </div>

            {teams.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginBottom: 10 }}>
                {teams.map((t, i) => (
                  <div key={t.teamId || `new-${i}`} style={{ border: "1px solid #e6e9f0", borderRadius: 8, padding: 10 }}>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <input
                        style={input}
                        value={t.name}
                        maxLength={40}
                        placeholder={`Team ${i + 1}`}
                        onChange={(e) => setTeam(i, { name: e.target.value })}
                      />
                      <input
                        style={{ ...input, width: 90 }}
                        value={t.color}
                        maxLength={20}
                        placeholder="Color"
                        onChange={(e) => setTeam(i, { color: e.target.value })}
                      />
                    </div>
                    <select
                      style={{ ...input, marginBottom: 6 }}
                      value={t.captainSub}
                      onChange={(e) => setTeam(i, { captainSub: e.target.value })}
                    >
                      <option value="">No captain yet</option>
                      {t.members.map((m) => (
                        <option key={m.userSub} value={m.userSub}>
                          Captain: {m.name || m.userSub}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 13, color: "#5b667a" }}>
                      {t.members.length ? t.members.map((m) => m.name || m.userSub).join(", ") : "No players yet"}
                    </div>
                    <button
                      type="button"
                      style={{ ...btn(false), marginTop: 6, padding: "4px 10px", fontSize: 12 }}
                      onClick={() => removeTeam(i)}
                    >
                      Remove team
                    </button>
                  </div>
                ))}
              </div>
            )}

            {teams.length > 0 && (
              pool.length === 0 ? (
                <p style={{ ...muted, marginBottom: 10 }}>
                  Players appear here once they have a counted game in this season.
                </p>
              ) : (
                <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #e6e9f0", borderRadius: 6, marginBottom: 10 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Player</th>
                        <th style={{ ...th, textAlign: "right" }}>Season wins</th>
                        <th style={th}>Team</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pool.map((p) => {
                        const a = assigned.get(p.userSub);
                        return (
                          <tr key={p.userSub}>
                            <td style={td}>{p.name || p.userSub}</td>
                            <td style={num}>{p.boardWins}</td>
                            <td style={td}>
                              <select
                                style={{ ...input, padding: "4px 6px" }}
                                value={a ? String(a.idx) : ""}
                                onChange={(e) => assign(p, e.target.value === "" ? -1 : Number(e.target.value))}
                              >
                                <option value="">— unassigned —</option>
                                {teams.map((t, i) => (
                                  <option key={t.teamId || `new-${i}`} value={String(i)}>
                                    {t.name || `Team ${i + 1}`}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}

            <div>
              <button type="button" style={btn(true)} disabled={teamsBusy || !teams.length} onClick={() => saveTeams()}>
                {teamsBusy ? "Saving…" : "Save teams"}
              </button>
              {savedTeams.length > 0 && (
                <button type="button" style={btn(false)} disabled={teamsBusy} onClick={() => saveTeams([])}>
                  Clear team play
                </button>
              )}
              <span style={muted}>
                {detail.teamStandings.length
                  ? `Live: ${detail.teamStandings.map((t) => `${t.name} ${t.boardWins}`).join(" · ")} (${detail.teamNightsPlayed} team nights)`
                  : "No team standings yet."}
              </span>
            </div>
          </div>
        );
      })()}

      {detail && (
        <>
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Standings</div>
            {detail.standings.length === 0 ? (
              <p style={{ ...muted, marginBottom: 0 }}>
                No counted games yet. Approve RSVPs on each night's board so games count.
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Player</th>
                    <th style={{ ...th, textAlign: "right" }}>Wins</th>
                    <th style={{ ...th, textAlign: "right" }}>W-L</th>
                    <th style={{ ...th, textAlign: "right" }}>Nights</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.standings.map((r) => (
                    <tr key={r.userSub}>
                      <td style={num}>{r.rank}</td>
                      <td style={td}>
                        {r.name || r.userSub}
                        {r.teamName && <span style={{ ...muted, marginLeft: 6 }}>· {r.teamName}</span>}
                      </td>
                      <td style={{ ...num, fontWeight: 700 }}>{r.boardWins}</td>
                      <td style={num}>{r.gameWins}-{r.gameLosses}</td>
                      <td style={num}>{r.nightsPlayed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Nights</div>
            {detail.nights.length === 0 ? (
              <p style={{ ...muted, marginBottom: 0 }}>No nights picked yet.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Night</th>
                    <th style={{ ...th, textAlign: "right" }}>Going</th>
                    <th style={{ ...th, textAlign: "right" }}>Approved</th>
                    <th style={{ ...th, textAlign: "right" }}>Counted</th>
                    <th style={{ ...th, textAlign: "right" }}>Pending</th>
                    <th style={{ ...th, textAlign: "right" }}>Disputed</th>
                    <th style={{ ...th, textAlign: "right" }}>Paid</th>
                    <th style={{ ...th, textAlign: "right" }}>Entry $</th>
                    <th style={th}>Night's best</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.nights.map((n) => (
                    <tr key={n.socialEventId}>
                      <td style={td}>
                        <a href={`/compete/${n.socialEventId}`} target="_blank" rel="noopener noreferrer">
                          {n.title || n.socialEventId}
                        </a>
                        <div style={{ fontSize: 12, color: "#8a93a3" }}>{n.date}</div>
                      </td>
                      <td style={num}>{n.rsvpYes}</td>
                      <td style={num}>{n.approvedPlayers}</td>
                      <td style={num}>{n.gamesCounted}</td>
                      <td style={{ ...num, color: n.gamesPending ? "#8a5a00" : undefined }}>{n.gamesPending}</td>
                      <td style={{ ...num, color: n.gamesDisputed ? "#b02a2a" : undefined }}>{n.gamesDisputed}</td>
                      <td style={num}>{n.entriesPaid}</td>
                      <td style={num}>{n.entryCollected ? `$${n.entryCollected}` : "—"}</td>
                      <td style={td}>{n.leaderName ? `${n.leaderName} (${n.leaderBoardWins})` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ ...muted, marginTop: 8 }}>
              Pending = games whose logger's RSVP isn't approved yet; open the night's board to approve.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SeasonsTab;
